(ns practitest.frontend.integration-settings.service-now-form
  (:require [practitest.frontend.init :as init]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common.service-now :as service-now]
            [re-frame.core :as re-frame]
            [practitest.frontend.integration-settings.entity-mapping.events :as events]
            [practitest.frontend.integration-settings.entity-mapping.subs :as subs]
            [practitest.frontend.integration-settings.shared-form :as shared-form]
            [practitest.frontend.components2.spinners :as spinners]))

(defn- service-now-mapping-item
  [{:keys [project current-mappings entity-type other-entity-enabled? all-types]}]
  (let [project-id      (name (:id project))
        locked?         (not (:accessible? project true))
        entity-label    (shared-form/get-entity-label entity-type)
        mapping         (get current-mappings (keyword project-id))
        entity-map      (get mapping entity-type)
        is-disabled?    (:disabled entity-map false)
        selected-ids    (set (:ex_type entity-map []))
        search-results  @(re-frame/subscribe [::subs/service-now-search-results-for project-id entity-type])
        search-loading? @(re-frame/subscribe [::subs/service-now-search-loading-for? project-id entity-type])
        active-pool     (or search-results all-types)
        types-pool      (->> (concat (filter #(contains? selected-ids (:id %)) all-types) active-pool)
                             (group-by :id)
                             (map (fn [[_ vs]] (first vs))))
        type-options    (shared-form/get-available-type-options types-pool current-mappings
                                                                 project-id entity-type other-entity-enabled?)
        handle-change    (fn [types]
                           (mixpanel/track "ServiceNow Integration Settings"
                                           {"Action" (str "Changed " entity-label " types")})
                           (re-frame/dispatch [::events/update-project-mapping project-id entity-type :ex_type types]))
        handle-toggle    (fn []
                           (mixpanel/track "ServiceNow Integration Settings"
                                           {"Action" (str (if is-disabled? "Enable" "Disable")
                                                          " " entity-label " synchronization")})
                           (re-frame/dispatch [::events/toggle-project-entity-status project-id entity-type]))
        handle-search    (fn [term]
                           (if (service-now/search-term-long-enough? term)
                             (events/dispatch-service-now-search-debounced project-id entity-type term)
                             (re-frame/dispatch [::events/clear-service-now-search-results project-id entity-type])))]
    [shared-form/project-mapping-row
     {:project-name     (:label project)
      :disabled?        is-disabled?
      :locked?          locked?
      :locked-tooltip   (format-translation (get-translation "bugtracker.integration-settings.no-permission.tooltip"
                                                             "You have no permissions in %{bugtracker} for this project")
                                            :bugtracker "ServiceNow")
      :selected-types   selected-ids
      :pt-entity        (shared-form/get-pt-entity-label entity-type)
      :type-options     type-options
      :placeholder      (format-translation (get-translation "bugtracker.integration-settings.select-item-types.placeholder"
                                                             "Select %{bugtracker} types")
                                            :bugtracker "ServiceNow")
      :on-types-change  handle-change
      :on-toggle        handle-toggle
      :on-search-change handle-search
      :search-loading?  search-loading?}]))

(defn service-now-entity-mapping-form []
  (let [loading?              @(re-frame/subscribe [::subs/loading?])
        projects-loading?     @(re-frame/subscribe [::subs/projects-loading?])
        types-loading?        @(re-frame/subscribe [::subs/service-now-types-loading?])
        requirements-enabled? @(re-frame/subscribe [::subs/requirements-sync-enabled?])
        issues-enabled?       @(re-frame/subscribe [::subs/issues-sync-enabled?])
        available-projects    @(re-frame/subscribe [::subs/available-projects])
        configured-projects   @(re-frame/subscribe [::subs/configured-projects])
        all-types             @(re-frame/subscribe [::subs/service-now-types])
        projects-for-selector (vec (map shared-form/transform-project-for-selector available-projects))
        entity-mapping        @(re-frame/subscribe [::subs/data])
        current-mappings      (:project_mappings entity-mapping)
        selected-projects     @(re-frame/subscribe [::subs/selected-project-ids])
        allow-issue-creation? (:allow_issue_creation entity-mapping false)
        use-pt-id?            (:use_pt_id entity-mapping false)
        skip-mapping?         (:skip_mapping_in_manual_import entity-mapping false)
        integration-name      @(re-frame/subscribe [::subs/integration-name])
        projects-for-section  (shared-form/build-projects-for-section selected-projects available-projects configured-projects)
        mapping-item-component (fn [props]
                                 [service-now-mapping-item (assoc props :all-types all-types)])]
    [:div.integration-form-content.service-now-entity-mapping-form
     [:div.form-group.integration-env
      [:label (format-translation (get-translation "bugtracker.integration-settings.env"
                                                   "%{bugtracker} Environment")
                                  :bugtracker "ServiceNow") ":"]
      [:input.form-control.project-name-input {:type     "text"
                                               :value    (str integration-name)
                                               :disabled true}]]
     (if (or loading? types-loading?)
       [:div.loading-container
        [spinners/mux-spinner {:size :medium}]]
       [:<>
        (if projects-loading?
          [:div.loading-container.projects-loading
           [spinners/mux-spinner {:size :small}]]
          [:<>
           [shared-form/project-selector
            {:projects-for-selector projects-for-selector
             :selected-projects     selected-projects
             :bugtracker            "ServiceNow"
             :on-change-fn          #(re-frame/dispatch [::events/update-selected-projects %])}]
           [shared-form/synchronization-section
            {:title                  (get-translation "bugtracker.integration-settings.requirements-synchronization.title"
                                                      "Enable Requirement Synchronization")
             :enabled?               requirements-enabled?
             :other-entity-enabled?  issues-enabled?
             :on-toggle              #(re-frame/dispatch [::events/toggle-requirements-sync %])
             :projects               projects-for-section
             :current-mappings       current-mappings
             :entity-type            :requirements
             :bugtracker             "ServiceNow"
             :mapping-item-component mapping-item-component}]
           [shared-form/synchronization-section
            {:title                  (get-translation "bugtracker.integration-settings.issues-synchronization.title"
                                                      "Enable Issue Synchronization")
             :enabled?               issues-enabled?
             :other-entity-enabled?  requirements-enabled?
             :on-toggle              #(re-frame/dispatch [::events/toggle-issues-sync %])
             :projects               projects-for-section
             :current-mappings       current-mappings
             :entity-type            :issues
             :bugtracker             "ServiceNow"
             :mapping-item-component mapping-item-component}]])
        [shared-form/general-settings-section
         {:allow-issue-creation?   allow-issue-creation?
          :use-pt-id?              use-pt-id?
          :skip-mapping?           skip-mapping?
          :bugtracker              "ServiceNow"
          :on-allow-issue-creation #(re-frame/dispatch [::events/toggle-allow-issue-creation %])
          :on-use-pt-id            #(re-frame/dispatch [::events/toggle-use-pt-id %])
          :on-skip-mapping         #(re-frame/dispatch [::events/toggle-skip-mapping-manual-import %])}]
        [shared-form/form-actions
         {:saving?   @(re-frame/subscribe [::subs/saving?])
          :dirty?    @(re-frame/subscribe [::subs/dirty?])
          :on-cancel #(re-frame/dispatch [::events/cancel-changes])
          :on-save   #(re-frame/dispatch [::events/save-settings])}]])]))

(defn service-now-integration-form-root [_]
  (fn [_]
    [service-now-entity-mapping-form]))

(defn ^:export init [webcomponent options]
  (init/init! {:options       options
               :init-event-fn (fn [config]
                                [[::events/initialize-db config]
                                 [::events/fetch-entity-mapping]
                                 [::events/fetch-available-projects]
                                 [::events/fetch-service-now-types]])
               :webcomponent  webcomponent
               :root-view     service-now-integration-form-root}))

(defn ^:dev/after-load remount []
  (init/remount-roots "service-now-integration-form-component"
                      service-now-integration-form-root))

(utils/register-webcomponent! "service-now-integration-form-component")
